import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { Observable, ReplaySubject } from 'rxjs';
import { takeUntil, take, map } from 'rxjs/operators';

import { AppState, Selectors } from 'store/root-reducer';
import * as CustomerActions from 'store/actions/customer.actions';

import { UserNotificationRow } from 'modules/shared/data-source/user-notifications.data-source';

import { SnackbarService } from 'app/services/snackbar.service';

import { UserNotificationsComponent } from 'modules/shared/components/user-notifications/user-notifications.component';
import { CustomersBatchUploadComponent } from '../../components/customers-batch-upload/customers-batch-upload.component';

import { CurrentUser } from 'modules/shared/models/account.models';
import { UploadCustomersBatchFileDispatchedAction, GetCurrentDomainDefaultNotifiedUsersPayload } from 'store/models/customer.models';
import { Domain } from 'modules/shared/models/domain.models';

@Component({
  selector: 'artemis-customers-batch-upload-page',
  templateUrl: './customers-batch-upload.page.html',
  styleUrls: ['./customers-batch-upload.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomersBatchUploadPage implements OnInit, OnDestroy {

  @ViewChild(UserNotificationsComponent, {static: false}) userOnboardingComponent: UserNotificationsComponent;
  @ViewChild(CustomersBatchUploadComponent, {static: false}) customersBatchUploadComponent: CustomersBatchUploadComponent;

  private unsubscribe$: ReplaySubject<any> = new ReplaySubject(1);

  currentUser: string;
  domain: Domain;

  isFileProcessing: Observable<boolean>;

  currentDomainDefaultUserList: Array<UserNotificationRow>;
  defaultUsersCardActive: boolean = true;
  defaultUsersListLoading$: Observable<boolean>;

  constructor(private router: Router,
              private store: Store<AppState>,
              private actions$: Actions,
              private cdRef: ChangeDetectorRef,
              private snackbar: SnackbarService) {
  }

  ngOnInit() {
    this.getLoadingStatus();

    this.getCurrentUser();
    this.prepareDefaultUsers();
    this.isFileProcessing = this.store.select(Selectors.customer.isUploadingCustomersBatchFile);
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private prepareDefaultUsers(): void {
    this.store.select(Selectors.domain.getCurrentDomain)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((domain: Domain) => {
        if (domain) {
          this.domain = domain;

          this.store.dispatch(new CustomerActions.GetDefaultNotifiedUsers(<GetCurrentDomainDefaultNotifiedUsersPayload>{
            domain: domain
          }));

          this.cdRef.detectChanges();
        }
      });

    this.store
      .select(Selectors.customer.getDefaultNotifiedUsers)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (defaultUsers: Array<UserNotificationRow>) => {
          this.currentDomainDefaultUserList = defaultUsers;
          this.checkCurrentUsersCard();
          this.cdRef.detectChanges();
        }
      );
  }

  private checkCurrentUsersCard(): void {
    if (!this.currentDomainDefaultUserList || !this.currentDomainDefaultUserList.length) {
      this.defaultUsersCardActive = false;
      this.cdRef.detectChanges();
      this.userOnboardingComponent.dataSource.selectCurrentUser(this.currentUser);
    } else if (this.domain) {
      this.defaultUsersCardActive = true;
      this.cdRef.detectChanges();
    }
  }

  private getCurrentUser(): void {
    this.store
      .select(Selectors.auth.getUser)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((currentUser: CurrentUser) => {
        this.currentUser = currentUser.sub;
        this.cdRef.detectChanges();
      });
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.defaultUsersListLoading$ = this.store.select(Selectors.customer.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((customerLoading: Array<CustomerActions.CustomerActionTypes>) => {
          return customerLoading.includes(CustomerActions.CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS) ||
                 customerLoading.includes(CustomerActions.CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS);
        })
      );
  }

  getNotifyUsers(): Array<UserNotificationRow> {
    if (this.defaultUsersCardActive) {
      return this.currentDomainDefaultUserList;
    } else {
      return this.userOnboardingComponent.dataSource.getSelectedUsers();
    }
  }

  processFile($event: File): void {
    const isNotifiedUserValid: boolean = this.getNotifyUsers().some((selectedUser: UserNotificationRow) => selectedUser.l1) &&
                                         this.getNotifyUsers().some((selectedUser: UserNotificationRow) => selectedUser.l2) &&
                                         this.getNotifyUsers().some((selectedUser: UserNotificationRow) => selectedUser.l3);

    if (!isNotifiedUserValid) {
      this.snackbar.error('Please make sure you have selected at least one L1, one L2 and one L3 user to be notified.');
      return;
    }

    const reqObject: UploadCustomersBatchFileDispatchedAction['payload'] = {
      file: $event,
      users: this.getNotifyUsers().map((user: UserNotificationRow) => user.oauthId),
      domains: [this.domain.id]
    };

    this.store.dispatch(new CustomerActions.UploadCustomersBatchFile(reqObject));

    this.actions$.pipe(
      ofType<CustomerActions.UploadCustomersBatchFileSuccess>(CustomerActions.CustomerActionTypes.UPLOAD_CUSTOMERS_BATCH_FILE_SUCCESS),
      take(1),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(() => this.customersBatchUploadComponent.clearUpload());
  }

  toggleUsersCards(): void {
    this.defaultUsersCardActive = !this.defaultUsersCardActive;
  }

  updateDefaultSettings(): void {
    this.router.navigateByUrl('account-settings');
  }
}
